import React, { useState } from "react";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link, Loader2 } from "lucide-react";
import { Ad } from "@/models/ad";
import { toast } from "sonner";

interface EditMaterialDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (adId: string, materialUrl: string) => Promise<void>;
  selectedAd: Ad | null;
}

const EditMaterialDialog: React.FC<EditMaterialDialogProps> = ({
  isOpen,
  onOpenChange,
  onSubmit,
  selectedAd
}) => {
  const [materialUrl, setMaterialUrl] = useState(selectedAd?.materialUrl || "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    if (selectedAd) {
      setMaterialUrl(selectedAd.materialUrl || "");
    }
  }, [selectedAd]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedAd) {
      toast.error("No hay anuncio seleccionado");
      return;
    }

    // Validar que sea una URL válida si se proporciona (validación más flexible)
    if (materialUrl.trim()) {
      const url = materialUrl.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        toast.error("La URL debe comenzar con http:// o https://");
        return;
      }
      
      if (!isValidUrl(url)) {
        toast.error("Por favor ingresa una URL válida");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      await onSubmit(selectedAd.id, materialUrl.trim());
      toast.success("Link del material actualizado correctamente");
      onOpenChange(false);
    } catch (error) {
      console.error("Error al actualizar material:", error);
      
      // Mostrar mensaje de error más detallado
      let errorMessage = "Error al actualizar el link del material";
      if (error instanceof Error) {
        errorMessage += `: ${error.message}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValidUrl = (string: string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  if (!selectedAd) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link className="h-5 w-5 text-blue-600" />
            Agregar Link del Material
          </DialogTitle>
          <DialogDescription>
            Agrega el enlace al material del anuncio para "{selectedAd.clientName}"
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="materialUrl" className="text-sm font-medium">
              URL del Material
            </Label>
            <Input 
              id="materialUrl"
              type="url"
              placeholder="https://ejemplo.com/imagen-o-video-del-anuncio"
              value={materialUrl}
              onChange={(e) => setMaterialUrl(e.target.value)}
              className="w-full"
              disabled={isSubmitting}
            />
            <p className="text-xs text-muted-foreground">
              Ingresa el enlace directo al material del anuncio (imagen, video, etc.)
            </p>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="min-w-[100px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                "Guardar"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditMaterialDialog; 